import React, { useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import NotificationCenter from '../components/NotificationCenter';
import ChangePasswordModal from '../components/ChangePasswordModal';
import GlobalSearch from '../components/GlobalSearch';
import AIChatWidget from '../components/AIChatWidget';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Menu, ChevronDown, Key, LogOut, Shield } from 'lucide-react';

const MainLayout = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);

  const handleLogoClick = () => {
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-slate-100">
      <Sidebar 
        isOpen={sidebarOpen} 
        onClose={() => setSidebarOpen(false)} 
        user={user}
      />
      
      {/* Header */}
      <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-slate-200">
        <div className="px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSidebarOpen(true)}
                data-testid="menu-button"
                className="p-2"
              >
                <Menu className="h-6 w-6" />
              </Button>
              <div 
                className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity"
                onClick={handleLogoClick}
                data-testid="logo-home-link"
              >
                <img 
                  src="/logo-transparent.png" 
                  alt="LSN Logo" 
                  className="h-10 w-10 object-contain flex-shrink-0"
                />
                <div className="hidden sm:block">
                  <h1 className="font-heading text-xl font-bold text-slate-900">
                    LSN Operations Dashboard
                  </h1>
                  <p className="text-sm text-slate-600">Welcome, {user.full_name}</p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <GlobalSearch />
              <NotificationCenter />
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    className="h-10"
                    data-testid="user-menu-button"
                  >
                    <span className="hidden sm:inline mr-2">{user.full_name}</span>
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem
                    onClick={() => setIsChangePasswordOpen(true)}
                    className="cursor-pointer"
                    data-testid="change-password-menu-item"
                  >
                    <Key className="h-4 w-4 mr-2" />
                    Change Password
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={onLogout}
                    className="cursor-pointer text-red-600 focus:text-red-600 focus:bg-red-50"
                    data-testid="logout-menu-item"
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Outlet />
      </main>

      <ChangePasswordModal
        isOpen={isChangePasswordOpen}
        onClose={() => setIsChangePasswordOpen(false)}
      />

      {/* Floating AI Assistant - available on every page */}
      <AIChatWidget user={user} />
    </div>
  );
};

export default MainLayout;
